<template>
    <div class="renderPage reporte-container animate-fade-in">
        <!-- Modern Toolbar -->
        <v-toolbar color="primary" dark flat class="mb-4 rounded-xl" height="64">
            <v-icon class="mr-3" size="28">mdi-shield-crown</v-icon>
            <v-toolbar-title class="text-subtitle-1 text-sm-h6 font-weight-black px-0 text-uppercase letter-spacing-1">
                Ctrl Ventas + Comisión SU
            </v-toolbar-title>
            <v-spacer />
            <v-btn icon color="white" to="/home" class="glass-btn">
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-toolbar>

        <!-- Filters Section - Glass Card -->
        <v-card class="glass-card mb-6 rounded-xl overflow-hidden" elevation="0">
            <div class="px-6 py-4 border-bottom header-glass">
                <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-filter-variant</v-icon>
                    <span class="text-subtitle-1 font-weight-bold primary--text">Filtros de Búsqueda</span>
                </div>
            </div>
            <v-card-text class="pa-6">
                <v-form ref="form" v-model="valid">
                    <v-row align="center">
                        <!-- Date Picker -->
                        <v-col cols="12" sm="6" md="4">
                            <v-dialog ref="dialog" v-model="dateModal" :return-value.sync="dates" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="dates" label="Período" prepend-inner-icon="mdi-calendar" readonly required :rules="dateRules" v-bind="attrs" v-on="on" outlined dense rounded hide-details class="modern-input"></v-text-field>
                                </template>
                                <v-date-picker v-model="dates" range color="primary" class="rounded-xl theme-aware-dialog">
                                    <v-spacer></v-spacer>
                                    <v-btn text color="primary" @click="dateModal = false" class="rounded-pill">Cancel</v-btn>
                                    <v-btn text color="primary" @click="$refs.dialog.save(dates)" class="rounded-pill font-weight-bold">OK</v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>

                        <!-- Users Select -->
                        <v-col cols="12" sm="4" md="3">
                            <v-select label="Usuarios" item-text="nombre" item-value="cedula" v-model="reporte.cedula" :items="usuarios" :disabled="!validUsuarios" outlined dense rounded hide-details prepend-inner-icon="mdi-account-group" class="modern-input"></v-select>
                        </v-col>

                        <!-- Actions -->
                        <v-col cols="12" sm="2" md="2" class="d-flex align-center">
                            <v-btn color="primary" @click="getReport" :disabled="!valid || visibleProgress" class="rounded-pill px-6 font-weight-bold shadow-soft" elevation="2" height="40">
                                <v-icon left>mdi-file-document-outline</v-icon>
                                Generar
                            </v-btn>
                        </v-col>
                        <v-col cols="12" sm="1" md="1" v-if="visibleProgress" class="d-flex align-center justify-center">
                            <v-progress-circular :size="30" :width="3" color="primary" indeterminate></v-progress-circular>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- Dynamic Summary Dashboard -->
        <v-expand-transition>
            <div v-if="validInfo" class="mb-6">
                <v-row>
                    <!-- Venta -->
                    <v-col cols="12" sm="6" md="2" class="d-flex">
                        <v-card class="glass-card flex-grow-1 rounded-xl d-flex flex-column" elevation="0">
                            <v-card-text class="d-flex flex-column align-center justify-center text-center pa-4 flex-grow-1">
                                <v-avatar color="primary" size="48" class="mb-3 dashboard-avatar">
                                    <v-icon color="white" size="24">mdi-cash-register</v-icon>
                                </v-avatar>
                                <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">Total Ventas</div>
                                <div class="text-body-2 font-weight-black primary--text">{{ venta.replace('Ventas: ', '') }}</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    
                    <!-- Premios -->
                    <v-col cols="12" sm="6" md="2" class="d-flex">
                        <v-card class="glass-card flex-grow-1 rounded-xl d-flex flex-column" elevation="0">
                            <v-card-text class="d-flex flex-column align-center justify-center text-center pa-4 flex-grow-1">
                                <v-avatar color="error" size="48" class="mb-3 dashboard-avatar">
                                    <v-icon color="white" size="24">mdi-gift</v-icon>
                                </v-avatar>
                                <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">Total Premios</div>
                                <div class="text-body-2 font-weight-black error--text">{{ premios.replace('Premios: ', '') }}</div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Comision -->
                    <v-col cols="12" sm="4" md="2" class="d-flex">
                        <v-card class="glass-card flex-grow-1 rounded-xl d-flex flex-column" elevation="0">
                            <v-card-text class="d-flex flex-column align-center justify-center text-center pa-4 flex-grow-1">
                                <v-avatar color="info" size="48" class="mb-3 dashboard-avatar">
                                    <v-icon color="white" size="24">mdi-percent</v-icon>
                                </v-avatar>
                                <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">Comisión</div>
                                <div class="text-body-2 font-weight-black info--text">{{ comisionAdmin.replace('Comisión: ', '') }}</div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Comision SU -->
                    <v-col cols="12" sm="4" md="3" class="d-flex">
                        <v-card class="glass-card flex-grow-1 rounded-xl d-flex flex-column" elevation="0">
                            <v-card-text class="d-flex flex-column align-center justify-center text-center pa-4 flex-grow-1">
                                <v-avatar color="amber darken-2" size="48" class="mb-3 dashboard-avatar">
                                    <v-icon color="white" size="24">mdi-account-star</v-icon>
                                </v-avatar>
                                <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">Comisión SU</div>
                                <div class="text-body-2 font-weight-black amber--text text--darken-2">{{ comisionSuperUsuario.replace('Comisión Super Usuario: ', '') }}</div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Total Admin -->
                    <v-col cols="12" sm="4" md="3" class="d-flex">
                        <v-card class="glass-card flex-grow-1 rounded-xl d-flex flex-column border-success" elevation="0">
                            <v-card-text class="d-flex flex-column align-center justify-center text-center pa-4 flex-grow-1">
                                <v-avatar color="success" size="48" class="mb-3 dashboard-avatar">
                                    <v-icon color="white" size="24">mdi-currency-usd</v-icon>
                                </v-avatar>
                                <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">Total Admin</div>
                                <div class="text-body-2 font-weight-black success--text">{{ totalAdmin.replace('Total Admin: ', '') }}</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </v-expand-transition>

        <!-- Data Table Container -->
        <v-expand-transition>
            <v-card class="glass-card rounded-xl overflow-hidden" elevation="0" v-if="validInfo">
                <div class="px-6 py-4 border-bottom header-glass d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                        <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                        <span class="text-subtitle-1 font-weight-bold primary--text">Ventas por sorteo</span>
                    </div>
                </div>
                <v-data-table :headers="headers" :items="items" class="premium-table" mobile-breakpoint="0">
                    <template v-slot:item.venta="{ item }">
                        <span class="font-weight-medium">{{ item.venta }}</span>
                    </template>
                    <template v-slot:item.premios="{ item }">
                        <span class="font-weight-medium error--text">{{ item.premios }}</span>
                    </template>
                    <template v-slot:item.comisionAdmin="{ item }">
                        <span class="font-weight-medium info--text">{{ item.comisionAdmin }}</span>
                    </template>
                    <template v-slot:item.comisionSuperUsuario="{ item }">
                        <span class="font-weight-medium amber--text text--darken-2">{{ item.comisionSuperUsuario }}</span>
                    </template>
                    <template v-slot:item.totalAdmin="{ item }">
                        <span class="font-weight-bold success--text">{{ item.totalAdmin }}</span>
                    </template>
                </v-data-table>
            </v-card>
        </v-expand-transition>
    </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
    components: {},
    data() {
        return {
            valid: false,
            validDecargar: false,
            validInfo: false,
            validUsuarios: false,
            dateModal: false,
            visibleProgress: false,
            dates: [],
            items: [],
            usuarios: [],
            venta: 'g',
            premios: 'g',
            comisionAdmin: 'g',
            comisionSuperUsuario: 'g',
            totalAdmin: 'g',
            dateRules: [
                (v) => v.length == 2 || "Fecha Inicial y Fecha Final Requeridas",
            ],
            headers: [
                {
                    text: "Sorteo",
                    align: "start",
                    value: "sorteo",
                },
                {
                    text: "Fecha",
                    align: "start",
                    value: "fechaSorteo",
                },
                {
                    text: "Venta",
                    align: "start",
                    value: "venta",
                },
                {
                    text: "Premios",
                    align: "start",
                    value: "premios",
                },
                {
                    text: "Comisión",
                    align: "start",
                    value: "comisionAdmin",
                },
                {
                    text: "Comisión Super Usuario",
                    align: "start",
                    value: "comisionSuperUsuario",
                },
                {
                    text: "Total Admin",
                    align: "start",
                    value: "totalAdmin",
                },
            ],
            reporte: {
                cedula: "",
                fechaInicio: new Date().toLocaleString("en-US", { timeZone: "America/Costa_Rica" }),
                fechaFin: new Date().toLocaleString("en-US", { timeZone: "America/Costa_Rica" }),
            },
        };
    },

    computed: {
        ...mapGetters(["user", "strCia"]),
    },

    created() {
        this.reporte.cedula = this.user.cedula;
        this.getUsuariosPorCia();
    },

    methods: {
        getReport() {
            this.visibleProgress = true;
            this.getReporteVentas();
            //this.getRecibidos();
        },

        getReporteVentas() {
            let me = this;
            axios
                .get(
                    "api/Tiempos/GetReporteControlVentasV2?idUsuario=" +
                    this.reporte.cedula +
                    "&fechaInicio=" +
                    this.dates[0].toString() +
                    "&fechaFin=" +
                    this.dates[1].toString() +
                    "&companyId=" +
                    this.strCia,
                )
                .then(function (response) {
                    me.visibleProgress = false;
                    me.items = response.data;
                    me.venta = "Ventas: " + response.data[0].venta;
                    me.premios = "Premios: " + response.data[0].premios;
                    me.comisionAdmin = "Comisión: " + response.data[0].comisionAdmin;
                    me.comisionSuperUsuario = "Comisión Super Usuario: " + response.data[0].comisionSuperUsuario;
                    me.totalAdmin = "Total Admin: " + response.data[0].totalAdmin;
                    me.validInfo = true;
                })
                .catch(function (error) {
                    console.log(error);
                });
        },

        getUsuariosPorCia() {
            let me = this;
            axios
                .get(
                    "api/Usuarios/getUsuariosPorCia?companyId=" +
                    this.strCia+"&usuarioId=" +
                    this.user.id,
                )
                .then(function (response) {
                    me.usuarios = response.data;
                    console.log(me.user.cedula);
                    const myUser = me.usuarios.find(user => user.cedula === me.user.cedula);
                    console.log(myUser.tipo);
                    if (myUser.tipo === true) {
                        me.reporte.cedula = "0";
                        me.validUsuarios = true;
                    } else {
                        me.reporte.cedula = myUser.cedula;
                    }
                })
                .catch(function (error) {
                    console.log(error);
                });
        },

        closed() {
            this.customerModal = false;
        },
    },
};
</script>

<style scoped>
.reporte-container {
    padding-bottom: 60px;
}

/* Glassmorphism Styles */
.glass-card {
    background: rgba(255, 255, 255, 0.7) !important;
    backdrop-filter: blur(12px) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.theme--dark .glass-card {
    background: rgba(30, 30, 46, 0.8) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.glass-btn {
    background: rgba(25, 118, 210, 0.1) !important;
}

.header-glass {
    background: rgba(25, 118, 210, 0.03);
}

.theme--dark .header-glass {
    background: rgba(255, 255, 255, 0.02);
}

.border-bottom {
    border-bottom: 1px solid rgba(0,0,0,0.05);
}

.theme--dark .border-bottom {
    border-bottom: 1px solid rgba(255,255,255,0.05);
}

/* Dashboard Metrics */
.dashboard-avatar {
    box-shadow: 0 4px 10px rgba(0,0,0,0.15);
    transition: transform 0.3s ease;
}

.glass-card:hover .dashboard-avatar {
    transform: scale(1.1);
}

.border-success {
    border-bottom: 4px solid var(--v-success-base) !important;
}

/* Modern Inputs */
.modern-input >>> .v-input__control > .v-input__slot {
    background: rgba(255, 255, 255, 0.5) !important;
    backdrop-filter: blur(4px);
}

.theme--dark .modern-input >>> .v-input__control > .v-input__slot {
    background: rgba(0, 0, 0, 0.2) !important;
}

/* Table Styling */
.premium-table {
    background: transparent !important;
}

.premium-table >>> th {
    background: rgba(0, 0, 0, 0.02) !important;
    font-weight: 800 !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.75rem !important;
}

.theme--dark .premium-table >>> th {
    background: rgba(255, 255, 255, 0.05) !important;
}

.premium-table >>> tr:hover {
    background: rgba(25, 118, 210, 0.05) !important;
}

/* Text Utilities */
.letter-spacing-1 {
    letter-spacing: 1px;
}

/* Animations */
.animate-fade-in {
    animation: fadeIn 0.4s ease-out;
}

.shadow-soft {
    box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

/* Theme Aware Dialog/Card */
.theme-aware-dialog {
    border-radius: 24px !important;
}

.theme-aware-card {
    background-color: var(--v-background-base, #ffffff) !important;
}

.theme--dark .theme-aware-card {
    background-color: var(--v-background-base, #1e1e2e) !important;
}
</style>